import classNames from 'classnames';
import Text from '../atoms/text';
import Button from '../atoms/button';
import { useTranslation } from 'react-i18next';

export interface ModalProps<T> extends React.HTMLAttributes<HTMLFormElement> {
	data: T;
	title: string;
	mode: 'create' | 'edit' | 'view';
	isModalVisible: boolean;
	onClose: (reload?: boolean) => void;
	containerClassName?: string;
	onConfirm?: () => void;
}

const Modal = <T,>({
	data,
	title,
	mode,
	isModalVisible,
	className,
	children,
	containerClassName,
	onConfirm,
	onClose,
	...props
}: ModalProps<T>) => {
	const { t } = useTranslation();

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		onConfirm?.();
	};

	const onOverlayClick = () => {
		onClose();
	};

	return (
		<div className="relative">
			{/* Overlay */}
			<div
				className={classNames(
					'fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 z-10 transition-opacity',
					{ hidden: !isModalVisible }
				)}
				aria-hidden={!isModalVisible}
				onClick={onOverlayClick}
			/>

			{/* Modal */}
			<form
				className={classNames(
					'fixed grid p-4 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-md shadow-lg max-w-[95%] w-[60rem] max-h-[95%] overflow-auto z-20',
					{
						hidden: !isModalVisible,
					},
					containerClassName
				)}
				autoComplete="off"
				onSubmit={handleSubmit}
				aria-modal={isModalVisible}
				role="dialog"
				{...props}
			>
				{/* Header */}
				<Text
					as="h3"
					className="text-primary border-b border-gray-border pb-1 font-bold"
				>
					{title}
				</Text>

				{/* Content */}
				<div className={classNames('grid py-6 gap-y-6', className)}>
					{children}
				</div>

				{/* Footer */}
				{mode !== 'view' && (
					<div className="flex gap-3 justify-self-end col-span-full">
						<Button theme="light" onClick={() => onClose()}>
							{t('misc.cancel')}
						</Button>
						<Button type="submit">{t('misc.save')}</Button>
					</div>
				)}
			</form>
		</div>
	);
};

export default Modal;
